// 📁 src/Header.js

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

export default function Header({
  scrollToId,
  showCTA = true,
  showLogin = true,
  currency,
  setCurrency,
  billingCycle,
  setBillingCycle
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [tenantId, setTenantId] = useState('');

  useEffect(() => {
    const hydrateCartCount = () => {
      const activeEmail =
        sessionStorage.getItem('isLoggedIn') === 'true' ? localStorage.getItem('email') : null;
      if (!activeEmail) return;

      setEmail(activeEmail);

      const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
      const hydratedTenantId = registry[activeEmail]?.tenantId || activeEmail;
      setTenantId(hydratedTenantId);

      const cartKey = `cartItems_${hydratedTenantId}`;
      const cart = JSON.parse(localStorage.getItem(cartKey) || '[]');
      const totalQty = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
      setCartCount(totalQty);
    };

    hydrateCartCount();
    window.addEventListener('storage', hydrateCartCount);
    const interval = setInterval(hydrateCartCount, 1000);

    return () => {
      window.removeEventListener('storage', hydrateCartCount);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const loginState = sessionStorage.getItem('isLoggedIn') === 'true';
    setIsLoggedIn(loginState);

    const activeEmail = localStorage.getItem('email');
    setEmail(activeEmail);

    const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
    const hydratedTenantId = registry[activeEmail]?.tenantId || '';
    setTenantId(hydratedTenantId);
  }, [location.pathname]);

  const handleLogout = () => {
    sessionStorage.clear();
    setIsLoggedIn(false);
    navigate('/');
  };

  const handleProductClick = (id) => {
    try {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        navigate(`/products#${id}`);
      }
    } catch {
      navigate(`/products#${id}`);
    }
  };

  const handleCurrencyChange = (e) => {
    const selectedCurrency = e.target.value;
    setCurrency(selectedCurrency);

    const activeEmail = localStorage.getItem('email');
    if (sessionStorage.getItem('isLoggedIn') === 'true' && activeEmail) {
      localStorage.setItem(`currency_${activeEmail}`, selectedCurrency);
      console.log(`💾 Currency saved to currency_${activeEmail}: ${selectedCurrency}`);
    } else {
      console.warn('⚠️ Currency write skipped: email not available');
      localStorage.setItem('currency_global', selectedCurrency);
    }
  };

  return (
    <header className="sticky top-0 z-[999] bg-white w-full shadow-md px-4 sm:px-6 py-4 flex items-center justify-between">
      <div className="flex flex-col">
        <Link to="/" className="text-2xl font-bold text-indigo-700">
          Talentraxion
        </Link>
      </div>

      <nav className="flex items-center space-x-4 sm:space-x-6 text-gray-700">
        <Link to="/" className="hover:text-white hover:bg-indigo-600 px-4 py-2 rounded transition">
          Home
        </Link>

        <div className="relative group">
          <button className="group-hover:text-white group-hover:bg-indigo-600 px-4 py-2 rounded transition">
            Products ▾
          </button>
          <div className="absolute left-0 mt-2 w-48 bg-white border rounded shadow-md z-[1000] hidden group-hover:block">
            {['ATS', 'Scout', 'PeopleSync', 'AuditReady', 'PerformEdge'].map((id) => (
              <button
                key={id}
                onClick={() => handleProductClick(id)}
                className="w-full text-left px-4 py-2 text-gray-700 hover:text-white hover:bg-indigo-600 transition"
              >
                {id}
              </button>
            ))}
          </div>
        </div>

        <select
          value={currency}
          onChange={handleCurrencyChange}
          className="border rounded px-2 py-1 text-sm bg-white hover:bg-indigo-50 transition"
        >
          <option value="USD">USD</option>
          <option value="BDT">BDT</option>
        </select>

        <div className="flex items-center border rounded px-2 py-1 text-sm bg-white hover:bg-indigo-50 transition">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-2 ${billingCycle === 'monthly' ? 'font-bold text-indigo-700' : 'text-gray-500'}`}
          >
            Monthly
          </button>
          <span className="mx-1 text-gray-400">|</span>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-2 ${billingCycle === 'annual' ? 'font-bold text-indigo-700' : 'text-gray-500'}`}
          >
            Annual
          </button>
        </div>

        {isLoggedIn && (
          <button
            onClick={() => navigate('/cart')}
            className="relative hover:text-white hover:bg-indigo-600 px-4 py-2 rounded transition"
          >
            🛒
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {cartCount}
              </span>
            )}
          </button>
        )}

        {showLogin && !isLoggedIn && (
          <button
            onClick={() => navigate('/signin')}
            className="hover:text-white hover:bg-indigo-600 px-4 py-2 rounded transition"
          >
            Sign In
          </button>
        )}

        {showLogin && isLoggedIn && (
          <div className="relative group">
            <button className="hover:text-white hover:bg-indigo-600 px-4 py-2 rounded transition">
              My Account ▾
            </button>
            <div className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-md z-[1000] hidden group-hover:block">
              <button
                onClick={() => navigate('/my-account/account-settings')}
                className="w-full text-left px-4 py-2 text-gray-700 hover:text-white hover:bg-indigo-600 transition"
              >
                Account Settings
              </button>
              <button
                onClick={() => navigate('/tenant-panel')}
                className="w-full text-left px-4 py-2 text-gray-700 hover:text-white hover:bg-indigo-600 transition"
              >
                Tenant Interface
              </button>
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-gray-700 hover:text-white hover:bg-indigo-600 transition"
              >
                Log Out
              </button>
            </div>
          </div>
        )}

        {showCTA && (
          <Link
            to="/register"
            className="bg-indigo-600 text-white px-4 py-2 rounded-full hover:bg-indigo-700 transition"
          >
            Get Started
          </Link>
        )}
      </nav>
    </header>
  );
}
